import * as React from 'react';

import { connect, Dispatch } from 'react-redux';
import { ICalendarAppState } from '../redux-state';

import { CalendarApi } from '../common/calendar-api';
import { IEvent, IMonth } from '../interfaces';
import { updateEvents, updateNextMonthEvents } from '../redux-state/actions/calendar.actions';
import CalendarText from '../contexts/calendar-text';
import { CalendarViewMode } from '../redux-state/interfaces/calendar-settings';
import moment = require('moment');

interface IExternalProps {
}

interface StateToProps {
    monthToLoad: string;
    updating: boolean;
    filterFunction: (event) => boolean;
    currentViewState: CalendarViewMode;
}

interface DispatchToProps {
    saveLoadedEvents: (loadedMonth: string, events: IMonth<IEvent>) => void;
    saveNextLoadedEvents: (loadedMonth: string, events: IMonth<IEvent>) => void;
}

export type LoaderProps = IExternalProps & StateToProps & DispatchToProps;

export interface LoaderState {
    loading: boolean;
    monthsLoaded: Array<string>;
}

/**
 * Utility class to load additional events.
 * 
 * Also displays a loader animation to prevent users from interacting with the app while data is changing.
 */
export class LoaderBase extends React.Component<LoaderProps, LoaderState> {
    constructor(props: LoaderProps) {
        super(props);
        this.state = {
            loading: this.props.monthToLoad.length > 0,
            monthsLoaded: this.props.monthToLoad.length > 0 ? [this.props.monthToLoad] : []
        };
    }
    componentDidMount() {
        if (this.state.loading) {
            this.loadFromApi();
        }
    }
    componentDidUpdate() {
        if (!this.state.loading || this.state.monthsLoaded.indexOf(this.props.monthToLoad) < 0) {
            if (this.props.monthToLoad.length > 0) {
                this.loadFromApi();
            }
        }
        const superParent = document.documentElement;

        if (this.props.updating) {
            // attribute removal happens in the views of the components that trigger loading states.
            // removal should happen after all other callback functionality.
            superParent.setAttribute("data-calendar-loading", "true");
        } else {
            superParent.removeAttribute("data-calendar-loading");
        }

    }
    loadFromApi() {
        this.setState({
            loading: true,
            monthsLoaded: this.state.monthsLoaded.indexOf(this.props.monthToLoad) < 0
                ? this.state.monthsLoaded.concat([this.props.monthToLoad]) : this.state.monthsLoaded
        }, () => {
            const startDate = this.props.monthToLoad;
            CalendarApi.getMonth(startDate, this.props.filterFunction).then(month => {
                this.props.saveLoadedEvents(this.props.monthToLoad, month);
                if (this.props.currentViewState != CalendarViewMode.GRID) {
                    this.setState({
                        loading: false
                    });
                }
            });
            const nextmonthMoment = moment(this.props.monthToLoad).add(1, 'month').format("YYYY-MM-DD");
            CalendarApi.getMonth(nextmonthMoment, this.props.filterFunction).then(month => {
                this.props.saveNextLoadedEvents(nextmonthMoment, month);
                this.setState({
                    loading: false
                });
            });
        });
    }
    render() {
        return (
            <CalendarText.Consumer>
                {text => (
                    <div className="ace-cal-loading">
                        <p className="visually-hidden" aria-live="polite">
                            {this.props.updating ? text.calendarLoadingLabel : text.calendarDoneLoadingLabel}
                        </p>
                        {
                            text.loaderImageUrl != "" ?
                                <div>
                                    <img src={text.loaderImageUrl}/>
                                </div>
                                :
                                <div className="ace-cal-loader-container" role="presentation">
                                    <div className="ace-cal-loader"></div>
                                </div>
                        }
                    </div>
                )}
            </CalendarText.Consumer>
        );
    }
}

function mapStateToProps(state: ICalendarAppState, ownProps: IExternalProps): StateToProps {
    return {
        monthToLoad: state.calendar.monthToLoad,
        updating: state.calendar.updating,
        filterFunction: state.calendar.filterFunction,
        currentViewState: state.calendarSettings.calendarViewMode
    }
}

function mapDispatchToProps(dispatch: Dispatch): DispatchToProps {
    return {
        saveLoadedEvents: (loadedMonth: string, events: IMonth<IEvent>) => { dispatch(updateEvents(loadedMonth, events)) },
        saveNextLoadedEvents: (loadedMonth: string, events: IMonth<IEvent>) => { dispatch(updateNextMonthEvents(loadedMonth, events)) }
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(LoaderBase);
